import { ComponentChild } from 'preact'
import { ContentType } from '@insided/shared/utils/contentType'

/* eslint-disable import/no-internal-modules */
import { ProductAreaType } from '@insided/deprecated-control/product-areas/ProductAreaSelect'
import { User } from '@insided/deprecated-preact-components/user-selector/User'
import { PaginationProps } from '@insided/deprecated-control/ui-components/PaginationComponent'

export interface Alert {
  message?: string
  type: 'success' | 'error'
}

export interface ModerationLabel {
  id: string
  title: string
}

export interface IdeaStatus {
  id: string
  name: string
  backgroundColor: string
  textColor: string
  visible: boolean
}

export interface Reputation {
  repliesMade: number
  likesReceived: number
  rank: string | null
  rankIcon: string | null
  rankIconThumbnail: string | null
}

export interface AuthorLite {
  id: string
  avatar: string
  username: string
}

export interface AuthorFromServer extends AuthorLite {
  customTitle: string
  signature: string
  reputation: Reputation
}

export interface Author extends AuthorLite {
  customTitle: string | null
  signature: string | null
  reputation: Reputation
}

export interface BulkAction {
  onClick: (data: Reply[] | undefined) => Promise<any>
}

export interface Reply {
  id: string
  publicReplyId: string
  content: string
  author: AuthorFromServer
  repliedAt: string
  trashed: boolean
  likedBy: string[]
  likedByOpener: boolean
  spam: boolean
  pendingApproval: boolean
}

export interface Topic {
  id: number
  publicId: string
  title: string
  submittedAt?: string
  createdAt?: string
  publishedAt?: string
  replyCount: number
  votes?: number
  likes?: number
  views: number
  categoryId: number
  moderatorTags: string[]
  votedBy?: string[]
  likedBy?: string[]
  tags: string[]
  sticky: boolean
  closed: boolean
  trashed: boolean
  content: string | null
  featuredImage?: string
  author: AuthorFromServer | Author
  productAreas: ProductAreaType[]
  replies?: Reply[]
  assignedModerator?: User
  moderationLabel: ModerationLabel
  ideaStatus: IdeaStatus
  pinnedReplyId?: string | null
  publicLabel: string | null
  spam: boolean
  pendingApproval: boolean
  url?: string
  contentType?: ContentType
  answer?: string
}

export interface TopicModerationProps {
  topic: Topic
  contentType: ContentType
  phrases?: {
    [key: string]: string
  }
  destinationUrl: string
  isProductboardNoteCreationEnabled: boolean
  isZendeskTicketEscalationEnabled: boolean
  isSalesforceCaseEscalationEnabled: boolean
  isFreshdeskTicketEscalationEnabled: boolean
  isIntercomCreateConversationEnabled: boolean
  categories: ParentCategory[]
  config: Config
}

export interface TopicModerationState extends Topic {
  showEditor: boolean
  isLoading: boolean | null
  isMarkAsSpamModalOpen: boolean
  isMarkAsNotSpamModalOpen: boolean
  selectedProductAreas: ProductAreaType[]
  allProductAreas: ProductAreaType[]
  selectedModerationLabel: ModerationLabel | null
  moderationLabels: ModerationLabel[]
  ideaStatuses: IdeaStatus[]
  alert: Alert | null
  message?: ComponentChild
  pinnedReply: Reply | null
  bestAnswer: Reply | null
  repliesPagination: PaginationProps
  repliesSorting: string
  selectedPublicLabel: string | null
  publicLabelText: string
  selectedReplyId?: string
  selectedReplies: string[]
  visibleReplyCount: number | null
}

export interface EscalationModalProps {
  modalTitle?: string
  modalTitleImage?: string
  closeModal: () => void
  isOpen: boolean
  title?: string
  username?: string
  content?: string | null
  titleValidationError?: string
  contentValidationError?: string
  onChangeCallback: (value: string) => void
  onContentChange: (value: string) => void
  onSubmit: () => void
  onTextareaFocus: (e?: MouseEvent | FocusEvent) => void
  onClickCallback: () => void
  isLoading: boolean
}

export interface ContentTypeConfig {
  assignModerator: boolean
  bestAnswer: boolean
  changeAuthor: boolean
  convertToArticle: boolean
  convertToConversation: boolean
  convertToIdea: boolean
  convertToProductUpdate: boolean
  convertToQuestion: boolean
  copyTopic: boolean
  featuredImage: boolean
  forum: boolean
  ideaStatus: boolean
  like: boolean
  markAsUnread: boolean
  moderationLabel: boolean
  moveReplyToExistingTopic: boolean
  moveReplyToNewTopic: boolean
  moveTopicToExistingTopic: boolean
  pending: boolean
  pendingReply: boolean
  pinReply: boolean
  productAreas: boolean
  publicLabel: boolean
  spam: boolean
  spamReply: boolean
  sticky: boolean
  trash: boolean
  trashReply: boolean
  versionHistory: boolean
  vote: boolean
}

export interface ModerationConfig {
  contentHelpfulness: boolean
  escalate: boolean
  escalateReply: boolean
  subscribe: boolean
}

export interface Config {
  contentTypeConfig: ContentTypeConfig
  moderationConfig: ModerationConfig
}

export const FORUM_TYPE = {
  COMMUNITY: 0,
  KNOWLEDGE_BASE: 1,
  IDEATION: 2,
  NEWS: 3,
  PUBLIC_GROUP: 4,
  PRIVATE_GROUP: 5,
  HIDDEN_GROUP: 6
}

export interface Category {
  categoryId: number
  categoryName: string
}

export interface ParentCategory {
  groupId: number
  groupName: string
  type: 0 | 1 | 2 | 3
  categories?: Category[]
}

export interface GroupedItems {
  groupName: string
  items: ItemsList
}

export interface Item {
  id?: string
  value?: string
  title: string
  image?: string
  onClick?: JSX.MouseEventHandler
  variant?: string
  href?: string
  class?: string
}

export type GroupedItemsList = GroupedItems[]

export type ItemsList = Item[]
export interface Group {
  groupId: number
  groupName: string
  type: typeof FORUM_TYPE.PUBLIC_GROUP | typeof FORUM_TYPE.PRIVATE_GROUP | typeof FORUM_TYPE.HIDDEN_GROUP
}
export interface Categories extends Array<ParentCategory | Group> {}
